import React from "react";
import styled from "styled-components/macro";
import List, { ListItem } from "../components/List";
import Text from "../components/Text";
import Table from "../components/Table";
import Heading from "../components/Heading";
import { Link } from "react-router-dom";
import DescriptionList, { DescriptionListDetail, DescriptionListTerm } from "../components/DescriptionList";
import { TERMS_AND_CONDITIONS_VERSION_NUMBER } from "../constants";

const JunoTermsVersion = styled.p`
  margin-top: 0;
  font-size: 1rem;
  color: var(--juno-color-text-secondary);
`;

function JunoTermsAndConditions() {
  return (
    <React.Fragment>
      <Text>
        <p>
          When you agree to these terms, you’re appointing us as your lawyers in order to buy, sell or remortgage a
          property, or some combination of the above.
        </p>

        <Heading level="3" id="who-we-are">
          Who we are
        </Heading>
        <p>
          We’re Juno Property Lawyers Limited, a law firm offering conveyancing services in England and Wales. We’re
          looking forward to working with you.
        </p>
        <p>
          We’re a team of 30+ conveyancers with more than 200 years of experience between us. We’re a mix of qualified
          Solicitors, Licenced Conveyancers, Chartered Legal Executives and non-qualified paralegals, all working
          together to make your move happen.
        </p>

        <Heading level="3" id="words-we-use-in-these-terms">
          Words we use in these terms
        </Heading>
        <p>In these terms, the following words and phrases have the following meanings:</p>
        <List bullet>
          <ListItem>
            ‘Client’ or ‘you’ means the person agreeing to these terms (if you’re accepting these terms on behalf of a
            company, ‘client’ or ‘you’ means the company in question).
          </ListItem>
          <ListItem>‘Juno’, ‘we’ or ‘us’ means Juno Property Lawyers Limited.</ListItem>
          <ListItem>‘Terms’ means these terms and conditions.</ListItem>
          <ListItem>‘Sale’ or ‘selling’ means a sale transaction.</ListItem>
          <ListItem>‘Purchase’ or ‘buying’ means a purchase transaction.</ListItem>
          <ListItem>‘Remortgage’ or ‘remortgaging’ means a remortgage transaction.</ListItem>
          <ListItem>
            ‘Complete’ or ‘completion’ means the point when we successfully complete your sale, purchase or remortgage
            in line with standard conveyancing practice.
          </ListItem>
          <ListItem>
            ‘Fall through’ or ‘fail’ means a sale to a given buyer or purchase from a given seller that did not
            exchange, or a remortgage of a given property with a given lender that did not complete.
          </ListItem>
        </List>

        <Heading level="3" id="who-can-agree-to-these-terms">
          Who can agree to these terms
        </Heading>

        <p>You’re agreeing to these terms on your own behalf.</p>
        <p>
          You can’t agree to these terms on anyone else’s behalf, unless you’re someone’s personal representative,
          you’re an executor, or you’ve been granted Power of Attorney. If this is the case we will ask for proof that
          you have authority to act on behalf of that person.
        </p>
        <p>
          If more than one person or company is appointing us, you agree to these terms both individually and as a group
          (‘jointly and severally’).
        </p>

        <Heading level="3" id="our-service">
          Our service
        </Heading>
        <p>We’ll act as your lawyers so you can buy, sell, or remortgage a property.</p>
        <p>Our role is to:</p>
        <List bullet>
          <ListItem>investigate who owns the property.</ListItem>
          <ListItem>agree contracts on a sale or purchase.</ListItem>
          <ListItem>obtain information and identify any potential issues.</ListItem>
          <ListItem>
            make sure your lender is happy to lend on the property (if you’re buying or remortgaging a property).
          </ListItem>
          <ListItem>transfer money.</ListItem>
          <ListItem>register your ownership (if you’re buying a property).</ListItem>
          <ListItem>
            register your new mortgage with the Land Registry (if you’re buying or remortgaging a property).
          </ListItem>
          <ListItem>
            make sure any old mortgages and loans are paid off (if you’re selling or remortgaging a property, as long as
            we’ve received the money).
          </ListItem>
          <ListItem>make any Stamp Duty payment on your behalf (as long as we’ve received the money).</ListItem>
        </List>
        <p>Your role is to:</p>
        <List bullet>
          <ListItem>make the final decision to buy, sell or remortgage the property.</ListItem>
          <ListItem>provide complete and truthful information about everything we ask for.</ListItem>
          <ListItem>tell us anything else you think is relevant.</ListItem>
        </List>
        <p>
          Every move is different, but you can read about each stage and how long they usually take at:{" "}
          <a href="https://www.juno.legal/blog/how-long-does-conveyancing-take" target="_blank" rel="noreferrer">
            www.juno.legal/blog/how-long-does-conveyancing-take
          </a>
        </p>
        <Heading level="4" id="giving-us-instructions">
          Giving us instructions
        </Heading>
        <p>
          When you complete our sign-up flow and instruct us, this is your instruction to us to get to work on your
          property transaction. We will not ask for an additional go-ahead for standard steps along the way. For
          example, we won’t wait for an additional go-ahead before ordering searches.
        </p>
        <p>
          You’ll get secure access to your online Juno dashboard, where you can see the progress of your transactions,
          send us documents, answer questions and get in touch via webchat. When you respond to a request on your
          dashboard, this constitutes a formal instruction to us. This includes giving us the final go-ahead
          (‘authority’) to exchange contracts on your behalf.
        </p>
        <p>
          If you’re buying or selling with someone else and one of you gives us the go-ahead to exchange contracts via
          the dashboard, we will take it as confirmation from all of you.
        </p>
        <p>
          We’ve tried to make our service accessible to everyone. If you need anything, please let us know and we’ll be
          happy to make reasonable adjustments.
        </p>
        <p>You can see more about the key stages and timescales at:</p>
        <List bullet>
          <ListItem>
            <a href="https://www.juno.legal/how-it-works/buying" target="_blank" rel="noreferrer">
              www.juno.legal/how-it-works/buying
            </a>
          </ListItem>
          <ListItem>
            <a href="https://www.juno.legal/how-it-works/selling" target="_blank" rel="noreferrer">
              www.juno.legal/how-it-works/selling
            </a>
          </ListItem>
        </List>
        <Heading level="4" id="searches">
          Searches
        </Heading>
        <p>Our search pack consists of the following:</p>
        <List bullet>
          <ListItem>Local authority search</ListItem>
          <ListItem>Water and drainage search</ListItem>
          <ListItem>Environmental search</ListItem>
          <ListItem>Chancel liability policy</ListItem>
        </List>
        <p>
          Other searches are not included. For example, we do not order a search covering planning applications in the
          local area.
        </p>
        <p>
          Searches are valid for six months and must be in date on completion. If your searches expire we’ll either need
          to order a new set, or buy search validation insurance, depending on your mortgage lender. The cost of this is
          not included.
        </p>
        <p>We cannot accept searches you’ve obtained elsewhere, such as a previous buyer or an estate agent.</p>
        <Heading level="4" id="mortgage-lenders">
          Mortgage lenders
        </Heading>
        <p>On a sale, we can work with any mortgage lender.</p>
        <p>On a purchase:</p>
        <List bullet>
          <ListItem>
            We can only work with the lenders listed at{" "}
            <a href="https://www.juno.legal/find-out-more/lenders/" target="_blank" rel="noreferrer">
              www.juno.legal/find-out-more/lenders
            </a>
            .
          </ListItem>
          <ListItem>
            If you’re taking out a mortgage with a different lender, you’ll have to use another firm for your move. If
            you’ve already started a transaction with us, we’ll send them all reasonable paperwork.
          </ListItem>
        </List>
        <Heading level="4" id="reporting-to-your-lender">
          Reporting to your lender
        </Heading>
        <p>
          If you’re buying with a mortgage, we act for your lender as well as for you. By accepting these terms, you
          authorise us to tell your lender anything we find out that we reasonably think would affect its decision to
          lend to you, or on what terms. For example, building work done without the right permissions, a problem with
          the title, or a change to the price. We will have to wait for the lender to confirm before we can proceed.
        </p>
        <p>
          Misleading a lender about the price or any incentive from the seller is mortgage fraud, and we have to tell
          your lender the true price.
        </p>
        <p>
          If you ask us not to report something, we won’t be able to keep working for you (see{" "}
          <a href="#if-we-cant-work-for-you">If we can’t work for you</a>).
        </p>
        <Heading level="4" id="changing_mortgage_offer">
          Changing your mortgage offer after exchange on a purchase
        </Heading>
        <p>
          You might want to get a new mortgage offer after we’ve exchanged contracts, for example, to get a better
          interest rate.
        </p>
        <p>
          We can do this as long as your lender stays the same (for example, your initial mortgage offer was with
          Barclays, and your new offer is still with Barclays). We must agree in writing before going ahead, since it’s
          possible to get into serious trouble if this goes wrong.
        </p>
        <p>
          We cannot accept a new offer from a different lender (for example, your initial mortgage offer was with
          Barclays but your new offer is with NatWest), because the conveyancing work is lender-specific.
        </p>

        <Heading level="4" id="whats-not-included">
          What’s not included
        </Heading>
        <p>
          Our role is limited to the standard legal work involved in the purchase, sale or remortgage of residential
          property.
        </p>
        <p>
          Our quote does not include anything that is not part of the legal work on a standard conveyancing transaction,
          for example:
        </p>
        <List bullet>
          <ListItem>Management company fees.</ListItem>
          <ListItem>
            Indemnity policies (other than the chancel liability indemnity policy, which is included on every property
            purchase).
          </ListItem>
          <ListItem>Extra searches, such as an additional flood or mining search.</ListItem>
          <ListItem>Tenant advice, such as how to give notice.</ListItem>
          <ListItem>
            Tax advice – see <a href="#tax">Tax</a> below.
          </ListItem>
          <ListItem>
            Legal disputes, such as taking a management company to court, or any post-completion problems.
          </ListItem>
        </List>

        <p>
          When two or more people are buying a property, the Land Registry transfer deed allows you to own equal shares
          or define a percentage split. We do not recommend any alternative arrangement or provide advice about
          alternatives (a ‘deed of trust’).
        </p>
        <p>
          Where you’re purchasing a leasehold property and we’ve advised you that there is an escalating ground rent, we
          strongly advise you to get independent valuation advice from a qualified surveyor to make sure that the
          escalating ground rent will not affect the value or saleability of the property.
        </p>
        <p>
          If you need a lease extension after you complete your purchase, we won’t be able to do that work for you, but
          we can suggest other firms you could use.
        </p>
        <p>No other people or organisations may rely upon the advice we give to you.</p>

        <Heading level="3" id="your-quote">
          Your quote
        </Heading>
        <p>We charge fixed fees for our services.</p>
        <p>
          At the start, we’ll give you a quote, and your quote will say what service you asked for. For example, ‘buy a
          freehold property’. If we find out that you’ve asked for a different service to the one you require, we’ll
          tell you and adjust our fee accordingly.
        </p>
        <Heading level="4" id="situations-where-your-quote-may-change">
          Situations where your quote can change
        </Heading>
        <p>We provide a quote based on the information you provide us.</p>
        <p>We’ll need to change your quote if:</p>
        <List bullet>
          <ListItem>
            The property is leasehold or leasehold with share of freehold, but our quote is for a freehold property.
          </ListItem>
          <ListItem>
            Your quote is for a freehold property, but you’re also buying something as a leasehold at the same time (eg
            a garage).
          </ListItem>
          <ListItem>
            We’re working for you as a company, but our quote is for working for you as an individual.
          </ListItem>
          <ListItem>The property is a buy-to-let, but our quote is for a standard residential purchase.</ListItem>
          <ListItem>
            The purchase involves a completely new lease, or we need to make changes to the property’s lease or transfer
            deeds (via a ‘deed of variation’):
            <List bullet>
              <ListItem>
                This is only needed on around 1 in 100 of our transactions, though it’s more common on leasehold
                properties.
              </ListItem>
              <ListItem>
                We will not be able to include this in our quote at the start because we only find out by doing a full
                review of the property documents, but we will let you know as soon as we can.
              </ListItem>
              <ListItem>
                Our fees are £600 inc VAT for a deed of variation and £750 inc VAT for a completely new lease.
              </ListItem>
              <ListItem>
                On a purchase, the Land Registry will charge a{" "}
                <Link
                  to="https://www.gov.uk/guidance/hm-land-registry-registration-services-fees#scale-2-fees"
                  target="_blank"
                >
                  {" "}
                  ‘scale 2’ fee
                </Link>
                , typically £30–£65.
              </ListItem>
            </List>
          </ListItem>
          <ListItem>
            You’re selling a retirement home – these come with extra legal agreements we need to review and act upon.
            <List bullet>
              <ListItem>Our fee for this work is £600 inc VAT.</ListItem>
              <ListItem>The retirement community management will typically also charge fees.</ListItem>
            </List>
          </ListItem>
        </List>
        <Heading level="4" id="our-quotes-are-valid-for-30-days">
          Our quotes are valid for 30 days
        </Heading>
        <p>
          If your quote is more than 30 days old at the point you tell us about your offer, we may need to update your
          quote based on our current pricing.
        </p>

        <Heading level="3" id="our-fees-on-your-sale-no-completion-no-fee">
          Our fees on your sale – no completion, no fee
        </Heading>
        <p>
          This section applies to all sellers <strong>except</strong> if you’ve been referred to Juno by one of the
          following estate agents: Allen & Harris, Barnfields, Brown & Merry, Connells, Hurfords, Kevin Henry, Pattison
          Lane, Quentin Marks, Roger Platt, Sharman Quinney and William H Brown.
        </p>
        <p>If your sale doesn’t complete, you won’t need to pay us.</p>
        <p>Here’s how it works:</p>
        <List bullet>
          <ListItem>You don’t have to pay us anything up front on your sale.</ListItem>
          <ListItem>
            On completion, we’ll transfer our full fee from our client account to our company account – we’ll provide
            you with an invoice as a record.
          </ListItem>
          <ListItem>
            If your sale falls through – for any reason at all – you won’t get charged for it. When you’re ready to try
            again, the same applies: nothing up front, and our fee only on completion.
          </ListItem>
        </List>
        <p>
          We’ll act on up to four sale attempts for a given property. If your sale falls through four times, we may not
          be able to act on a further attempt.
        </p>
        <p>
          If you’re buying and selling, your next purchase cannot be a new build, shared ownership, involve a mortgage
          lender we can’t work with, or involve any other service we do not offer – there’s a full list under{" "}
          <a href="#services-we-do-not-offer">Services we do not offer</a> below. If you choose to proceed with a second
          purchase we cannot handle, we will not be able to handle your sale either (unless you do not need both
          transactions to complete on the sale day), and we’ll give you a refund for any amount not yet billed, and keep
          any amounts already invoiced.
        </p>
        <p>
          Normally we’ll deduct our fee from the sale proceeds on completion, but if this isn’t enough, you’ll need to
          cover any shortfall before completion.
        </p>
        <p>
          Work on your sale is bespoke to you, your property, and your sale. You agree to waive your right to a
          cooling-off period so we can begin work right away.
        </p>
        <p>
          In the unlikely event that your sale exchanges but does not complete, you will still need to pay our quoted
          fee in full.
        </p>

        <Heading level="3" id="our-fees-on-your-sale-for-clients-referred-by-sequence-home-conveyancing">
          Our fees on your sale – for clients referred by Home Conveyancing
        </Heading>
        <p>
          This section applies to all sellers <strong>referred to Juno by Home Conveyancing.</strong> The following
          estate agents refer via Home Conveyancing:
        </p>
        <List bullet>
          <ListItem>Allen & Harris</ListItem>
          <ListItem>Barnfields</ListItem>
          <ListItem>Brown & Merry</ListItem>
          <ListItem>Connells</ListItem>
          <ListItem>Hurfords</ListItem>
          <ListItem>Kevin Henry</ListItem>
          <ListItem>Pattison Lane</ListItem>
          <ListItem>Quentin Marks</ListItem>
          <ListItem>Roger Platt</ListItem>
          <ListItem>Sharman Quinney</ListItem>
          <ListItem>William H Brown</ListItem>
        </List>
        <p>If you were referred by Home Conveyancing:</p>
        <List bullet>
          <ListItem>You don’t have to pay us anything up front on your sale.</ListItem>
          <ListItem>
            On completion, we’ll transfer our full fee from our client account to our company account – we’ll provide
            you with an invoice as a record.
          </ListItem>
          <ListItem>
            If your sale falls through, we will not charge our legal fee. We’ll give you a new quote when you’re ready
            to start your next sale – the pricing from your previous sale does not automatically apply.
          </ListItem>
        </List>
        <p>
          Normally we’ll deduct our fee from the sale proceeds on completion, but if this isn’t enough, you’ll need to
          cover any shortfall before completion.
        </p>

        <Heading level="3" id="our-fees-on-your-purchase-and-our-second-chance-guarantee-for-buyers">
          Our fees on your purchase – and our second chance guarantee for buyers
        </Heading>
        <p>
          This section applies to all buyers <strong>except</strong> if you’ve been referred to Juno by one of the
          following estate agents: Allen & Harris, Barnfields, Brown & Merry, Connells, Hurfords, Kevin Henry, Pattison
          Lane, Quentin Marks, Roger Platt, Sharman Quinney and William H Brown.
        </p>
        <p>
          With our second chance guarantee, if your first purchase falls through you can have another try within 12
          months without paying anything more.
        </p>
        <Heading level="4">How it works</Heading>
        <List bullet>
          <ListItem>
            At the start of your first purchase, you’ll pay your full fee up front and the money will arrive in our
            client account.
          </ListItem>
          <ListItem>
            We’ll transfer money to our company account at the following milestones:
            <Table>
              <thead>
                <tr>
                  <th>Milestone reached</th>
                  <th>Amount deducted from client account, inc VAT</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td data-title="Milestone reached: ">1. Searches ordered</td>
                  <td data-title="Amount deducted from client account, inc VAT: ">£399</td>
                </tr>
                <tr>
                  <td data-title="Milestone reached: ">2. Title and local search reviewed, initial enquiries raised</td>
                  <td data-title="Amount deducted from client account, inc VAT: ">75% of our remaining fee</td>
                </tr>
                <tr>
                  <td data-title="Milestone reached: ">3. Purchase completed</td>
                  <td data-title="Amount deducted from client account, inc VAT: ">100% of our remaining fee</td>
                </tr>
              </tbody>
            </Table>
          </ListItem>
          <ListItem>
            When we transfer money to our company account, we’ll provide you with an invoice as a record of the payment.
            These transfers are non-refundable.
          </ListItem>
          <ListItem>
            If your first purchase falls through – for any reason at all – your second purchase will be covered by our
            second chance guarantee, as long as you start it within 12 months of the date your first purchase fell
            through.
          </ListItem>
          <ListItem>
            With our second chance guarantee, we’ll get you the searches and start work on the new property at no extra
            charge. Any amounts already transferred to our company account on your first purchase will not be
            transferred again on your second purchase, so you won’t be out of pocket.
          </ListItem>
          <ListItem>
            If your second purchase falls through (or if your first purchase falls through and you don’t proceed with a
            second purchase within 12 months) Juno will keep any amounts already transferred to our company account and
            refund you any amount still in our client account.
          </ListItem>
          <ListItem>
            If your first purchase falls through and you request a refund for the amount in our client account, then
            your second purchase will not be covered by our second chance guarantee. You’ll pay your full fee up front
            into our client account, and we’ll transfer it into our company account according to the milestones above.
          </ListItem>
          <ListItem>
            At the start of your third or subsequent purchase (or second purchase if you do not start it within 12
            months) you’ll pay your full fee up front into our client account, and we’ll transfer it into our company
            account according to the milestones above. If your third or subsequent purchase falls through, Juno will
            keep any amounts already transferred to our company account, and we’ll refund you any amount still in our
            client account.
          </ListItem>
          <ListItem>
            If you want us to work on purchasing two different properties at the same time, you will not be covered by
            our second chance guarantee. You’ll pay the full fee up front for both properties into our client account,
            and we’ll transfer the money into our company account for both transactions according to the milestones
            above.
          </ListItem>
        </List>
        <p>
          To use your second chance guarantee, your second purchase cannot be a new build, shared ownership, involve a
          mortgage lender we can’t work with, or involve any other service we do not offer – there’s a full list under{" "}
          <a href="#services-we-do-not-offer">Services we do not offer</a> below. If you choose to proceed with a second
          purchase we cannot handle, we’ll give you a refund of everything except the first £399 for searches and setup.
          In this situation we will not be able to handle your sale (unless you do not need both transactions to
          complete on the sale day).
        </p>
        <p>
          Work on your purchase is bespoke to you, your chosen property, and your purchase. You agree to waive your
          right to a cooling-off period so we can begin work right away.
        </p>
        <p>
          In the unlikely event that your purchase exchanges but does not complete, you will still need to pay the
          remainder of our quoted fee.
        </p>

        <Heading level="4">Mortgage offer protection</Heading>
        <p>
          If your quote includes ‘mortgage offer protection’, we’ll give you a full refund on the legal fees for your
          purchase if your lender doesn’t approve your mortgage offer.
        </p>
        <p>To get the refund:</p>
        <List bullet>
          <ListItem>
            You must not be borrowing more than 90% of the property’s value, or more than 5× your combined annual
            income.
          </ListItem>
          <ListItem>
            If you can’t get a mortgage due to an adverse mortgage valuation, the valuation must be less than 95% of the
            agreed offer.
          </ListItem>
          <ListItem>
            You’ll need to provide evidence showing that your mortgage application has been rejected (such as a letter
            from a lender).
          </ListItem>
          <ListItem>Your quote must include mortgage offer protection.</ListItem>
        </List>
        <p>
          If your first purchase falls through because of your mortgage offer, you cannot get a refund and also use our
          second chance guarantee – you must choose which one you want.
        </p>

        <Heading level="3" id="our-fees-on-your-purchase-for-clients-referred-by-sequence-home-conveyancing">
          Our fees on your purchase – for clients referred by Home Conveyancing
        </Heading>
        <p>
          This section applies to all sellers <strong>referred to Juno by Home Conveyancing.</strong> The following
          estate agents refer via Home Conveyancing:
        </p>
        <List bullet>
          <ListItem>Allen & Harris</ListItem>
          <ListItem>Barnfields</ListItem>
          <ListItem>Brown & Merry</ListItem>
          <ListItem>Connells</ListItem>
          <ListItem>Hurfords</ListItem>
          <ListItem>Kevin Henry</ListItem>
          <ListItem>Pattison Lane</ListItem>
          <ListItem>Quentin Marks</ListItem>
          <ListItem>Roger Platt</ListItem>
          <ListItem>Sharman Quinney</ListItem>
          <ListItem>William H Brown</ListItem>
        </List>
        <p>If you were referred by Home Conveyancing:</p>
        <List bullet>
          <ListItem>
            You’ve already entered into a contract with your estate agent to provide you with searches. If you’ve not
            paid them yet, you’ll need to pay us £399 inc VAT and we’ll transfer this across. You’ll be covered by the
            Home Conveyancing search pledge – your estate agent should already have provided you with their terms.
          </ListItem>
          <ListItem>
            You don’t have to pay us anything up front on your purchase (except for the £399 to your estate agent, as
            above).
          </ListItem>
          <ListItem>
            On completion, we’ll transfer our full fee from our client account to our company account – we’ll provide
            you with an invoice as a record.
          </ListItem>
          <ListItem>
            If your purchase falls through, we will not charge our legal fee. We’ll give you a new quote when you’re
            ready to start your next purchase – the pricing from your previous purchase does not automatically apply.
          </ListItem>
        </List>

        <Heading level="3" id="if-tax-rates-or-government-fees-change">
          If tax rates or government fees change
        </Heading>
        <p>If the VAT rate changes, your quote will not change.</p>
        <p>
          If the Stamp Duty rates change, your Stamp Duty amount will change. The Stamp Duty amount on your quote is an
          estimate based on the Stamp Duty rates at the time and other information you have given us.
        </p>
        <p>
          If the Land Registry fee increases after you’ve sent us the money and before we register your new mortgage,
          we’ll ask you to send us the additional amount. If the fee decreases, we’ll refund you the difference once
          your registration is complete.
        </p>

        <Heading level="3" id="tax">
          Tax
        </Heading>
        <p>
          If you’re buying a property, we’ll prepare and submit your tax return, and pay the tax on your behalf (as long
          as we’ve received the money).
        </p>
        <p>
          In England, Stamp Duty is payable to HMRC. In Wales, Land Transaction Tax (LTT) is payable to the Welsh
          Revenue Authority (WRA).
        </p>
        <p>
          We’ll work out your tax due using the HMRC and WRA published rates, based on the information you give us:
          whether you own another property, whether you’re a first-time buyer, and whether you live in the UK. You’re
          responsible for making sure that information is complete and accurate: although we file the return for you,
          it’s submitted in your name and you’re legally responsible for its contents.
        </p>
        <p>
          Because we submit Stamp Duty returns for our clients, the law requires us to be registered with HMRC as a tax
          adviser. This registration is a legal requirement for anyone who files tax returns on someone else’s behalf,
          but it does not mean we can give tax advice.
        </p>
        <p>
          We cannot give tax advice. This is because we are not authorised to provide tax advice by our regulator, the
          CLC, meaning that tax advice would not be covered by our insurance or the CLC Compensation Fund. This means we
          cannot advise you on tax reliefs or exemptions, ways to reduce your tax, or on any other taxes (such as
          Capital Gains Tax, Inheritance Tax or Income Tax). If your situation isn’t straightforward — for example,
          you’re not sure whether the higher rates apply to you, or you think a relief might — you should get advice
          from a tax specialist or accountant before exchange.
        </p>

        <Heading level="3" id="third-party-costs">
          Third party costs
        </Heading>
        <p>Our quote includes several third party costs as standard:</p>
        <List bullet>
          <ListItem>ID checks</ListItem>
          <ListItem>Bank transfer fees</ListItem>
          <ListItem>Land Registry documents</ListItem>
          <ListItem>
            Searches: local, water and drainage, and environmental reports, if you’re buying a property
          </ListItem>
          <ListItem>Chancel liability insurance, if you’re buying a property</ListItem>
          <ListItem>
            Bankruptcy searches and the Land Registry ‘official search with priority’, if you’re buying or remortgaging
          </ListItem>
        </List>
        <p>Sometimes we’ll have to pay for something on your behalf.</p>
        <p>These costs are also known as ‘disbursements’.</p>
        <p>
          For example, if you’re selling a property where you’ve built an extension and you’ve lost the Planning
          Permission, we can get a copy from the local council, and we’ll pass the cost on to you.
        </p>
        <p>We don’t add a handling fee or mark these payments up in any way.</p>
        <p>
          If you cancel your contract with us, if it becomes apparent that a transaction has failed, or if we find out
          we can’t work for you:
        </p>
        <List bullet>
          <ListItem>
            We usually won’t be able to refund any money you’ve paid us for third-party costs, because we usually spend
            the money soon after you pay it to us.
          </ListItem>
          <ListItem>
            If you’ve paid us money for third party costs and we haven’t yet spent it, we’ll refund it to you.
          </ListItem>
          <ListItem>
            If we’ve spent money on your behalf and you <em>haven’t</em> yet paid it to us, you’ll have to send it to us
            immediately.
          </ListItem>
        </List>

        <Heading level="3" id="referrals">
          Referrals
        </Heading>
        <p>
          Some of our clients come to us through a referral from an estate agent, mortgage broker, lender or another
          business, or through a recommendation from a previous client.
        </p>
        <p>
          Where that happens we may pay the person or business who referred you a fee or give them a reward. That never
          changes what we charge you.
        </p>
        <p>
          Our client referral terms are at{" "}
          <a href="https://www.juno.legal/client-referral-terms/" target="_blank" rel="noopener noreferrer">
            https://www.juno.legal/client-referral-terms/
          </a>
        </p>
        <p>
          Our estate agent and broker referral terms are at{" "}
          <a href="https://www.juno.legal/agent-and-broker-referral-terms/" target="_blank" rel="noopener noreferrer">
            https://www.juno.legal/agent-and-broker-referral-terms/
          </a>
        </p>
        <p>
          We may pay referral fees to your estate agent or mortgage broker (in the form of cash and/or vouchers). If we
          do this the average fee we pay is £250 + VAT. You have the freedom to choose your conveyancer, so if you don’t
          want to use Juno you’re free to use another provider.
        </p>

        <Heading level="3" id="if-we-cant-work-for-you">
          If we can’t work for you
        </Heading>
        <p>
          If we find out that we can’t work for you, we’ll let you know as soon as possible. Reasons we might not be
          able to work for you include:
        </p>
        <List bullet>
          <ListItem>
            The work you’re asking us to do is something we do not offer – there’s a list in the next section.
          </ListItem>
          <ListItem>
            The work you’re asking us to do includes things that are not part of the standard legal work involved in the
            purchase, sale or remortgage of residential property.
          </ListItem>
          <ListItem>
            We’re already working for the people on the other side of your transaction and they’ve not given permission
            for us to work for both sides (for example, you’re buying a property, and we’re already working for the
            sellers, either on this transaction, or another one). This is known as a ‘conflict of interest’.
          </ListItem>
          <ListItem>
            We find out that someone is pressuring someone else to take part in the transaction. This is known as ‘undue
            influence’ or ‘under duress’.
          </ListItem>
          <ListItem>
            In cases where the property is owned jointly, we are given conflicting instructions by each owner or are
            asked by one owner to withhold information from the other owner.
          </ListItem>
          <ListItem>
            We’re not on your mortgage lender’s panel – our full list is here:{" "}
            <a href="https://www.juno.legal/find-out-more/lenders/" target="_blank" rel="noreferrer">
              www.juno.legal/find-out-more/lenders
            </a>
          </ListItem>
          <ListItem>
            You ask us not to tell your mortgage lender something we need to report (see{" "}
            <a href="#reporting-to-your-lender">Reporting to your lender</a>).
          </ListItem>
          <ListItem>You do not pass our ID checks.</ListItem>
          <ListItem>
            We cannot verify that your money has come from legitimate sources, and you do not have any alternative
            sources for the money.
          </ListItem>
          <ListItem>
            You can’t understand English. As a law firm, we’re required to make sure that you can understand the
            information we’re giving you and the choices we’re presenting you with. If you can’t understand English,
            you’ll need to find a lawyer that can communicate with you in a language you understand.
          </ListItem>
          <ListItem>
            We find out that the other party (the person buying your property or selling one to you) has decided to
            represent themselves. In these cases, we won’t be able to offer you satisfactory levels of protection
            against things like identity fraud or money laundering.
          </ListItem>
          <ListItem>The property is not in England and Wales.</ListItem>
          <ListItem>We’re already at full capacity.</ListItem>
        </List>
        <p>We reserve the right to refuse to work for you (at our sole and absolute discretion).</p>

        <Heading level="3" id="services-we-do-not-offer">
          Services we do not offer
        </Heading>

        <p>
          Our role is limited to the standard legal work involved in the purchase, sale or remortgage of residential
          property.
        </p>
        <p>
          If you’re buying and selling, or buying and remortgaging, we will only handle it if we’re doing both parts.
          The one exception is if you do not need both transactions to complete on the same day. If we find that you are
          using Juno for one transaction and another firm for the other (for example, we act on your purchase, but not
          your sale), we reserve the right to stop working for you.
        </p>
        <p>We do not handle:</p>
        <List bullet>
          <ListItem>
            <strong>Agricultural property</strong>
          </ListItem>
          <ListItem>
            <strong>Armed Forces Help to Buy loans</strong>
          </ListItem>
          <ListItem>
            <strong>Auctions</strong> – including modern auctions
          </ListItem>
          <ListItem>
            <strong>Buying with a bridging loan</strong>
          </ListItem>
          <ListItem>
            Properties in the <strong>Channel Islands</strong>
          </ListItem>
          <ListItem>
            <strong>Commercial property</strong>
          </ListItem>
          <ListItem>
            Acting for a <strong>company</strong>
          </ListItem>
          <ListItem>
            Buying from or selling to a company <strong>based outside the UK</strong>
          </ListItem>
          <ListItem>
            <strong>Compulsory purchases</strong>
          </ListItem>
          <ListItem>
            Acting where <strong>contracts have already been exchanged</strong>
          </ListItem>
          <ListItem>
            <strong>Contract races</strong> – where a seller has accepted more than one offer
          </ListItem>
          <ListItem>
            <strong>Criss-cross leases</strong>
          </ListItem>
          <ListItem>
            <strong>Deeds of gift</strong> – giving a property to someone with no money changing hands
          </ListItem>
          <ListItem>
            Drafting a <strong>Deed of Trust</strong> on a purchase – an agreement as to how to split proceeds when a
            property is sold
          </ListItem>
          <ListItem>
            Buying or selling at a <strong>discounted price</strong>
          </ListItem>
          <ListItem>
            <strong>Divorce</strong>
          </ListItem>
          <ListItem>
            Buying from an <strong>equity release company</strong>
          </ListItem>
          <ListItem>
            <strong>Equity release mortgages</strong> on a purchase
          </ListItem>
          <ListItem>
            <strong>Exchange deadlines</strong> imposed by a bank, property developer or asset manager
          </ListItem>
          <ListItem>
            <strong>Exclusivity or reservation agreement</strong> – prevents the seller from selling to anyone else for
            a fixed amount of time – requires drafting and negotiating an entirely separate contract
          </ListItem>
          <ListItem>
            <strong>Buying with a Help to Buy loan</strong> – selling with one is fine
          </ListItem>
          <ListItem>
            <strong>Holding deposit</strong> – as in, a deposit in addition to the mortgage deposit and exchange deposit
            – requires drafting and negotiating an entirely separate contract
          </ListItem>
          <ListItem>
            Buying a property to use as a <strong>holiday let</strong> – lenders and councils often have special
            restrictions
          </ListItem>
          <ListItem>
            Buying on a <strong>holiday park</strong>
          </ListItem>
          <ListItem>
            <strong>House in Multiple Occupation (HMO)</strong> – a house split into several individual bedsits or
            flats) unless you’ve been referred to Juno directly by a specialist lender
          </ListItem>
          <ListItem>
            <strong>Islamic or Sharia mortgages</strong> – but we can accept money from an Islamic or Sharia savings
            account.
          </ListItem>
          <ListItem>
            <strong>Land only</strong>
          </ListItem>
          <ListItem>
            <strong>Lease extensions</strong>, except:
            <List bullet>
              <ListItem>
                On a sale where you’ve already agreed terms with the freeholder, and it’s not a statutory lease
                extension.
              </ListItem>
              <ListItem>
                On a purchase where the seller has already agreed the new lease or deed of variation with the
                freeholder, and it’s not a statutory lease extension.
              </ListItem>
            </List>
          </ListItem>
          <ListItem>
            <strong>Leaseholds which involve an unregistered freehold</strong>
          </ListItem>
          <ListItem>
            <strong>Lifetime mortgages</strong> on a purchase, or as the new mortgage on a remortgage
          </ListItem>
          <ListItem>
            <strong>Linked transactions where a different law firm is handling the other half</strong> – for example,
            we’re handling the sale and another firm are handling the purchase, or we’re handling your purchase and
            another firm is handling a remortgage you’re using to pay for the purchase
          </ListItem>
          <ListItem>
            <strong>Loans from a non-FCA-regulated lender</strong> where you want us to advise on the loan, and/or it’s
            to be registered against the property as a charge or restriction.
          </ListItem>
          <ListItem>
            <strong>Lockout agreement</strong> – prevents the seller from selling to anyone else for a fixed amount of
            time – requires drafting and negotiating an entirely separate contract
          </ListItem>
          <ListItem>
            <strong>Matrimonial home rights notices</strong>
          </ListItem>
          <ListItem>
            <strong>Mobile homes</strong>
          </ListItem>
          <ListItem>
            <strong>Taking out a mortgage where we’re not on the panel</strong> – our full list is here:{" "}
            <a href="https://www.juno.legal/find-out-more/lenders/" target="_blank" rel="noreferrer">
              www.juno.legal/find-out-more/lenders
            </a>
          </ListItem>
          <ListItem>
            <strong>Mortgages for students or university</strong>
          </ListItem>
          <ListItem>
            <strong>New builds</strong>
          </ListItem>
          <ListItem>
            <strong>New leases</strong>
          </ListItem>
          <ListItem>
            <strong>Non-refundable deposits</strong> – as in, a deposit in addition to the mortgage deposit and exchange
            deposit – requires drafting and negotiating an entirely separate contract
          </ListItem>
          <ListItem>
            Properties in <strong>Northern Ireland</strong>
          </ListItem>
          <ListItem>
            <strong>Option agreements</strong> – an agreement giving a buyer the option to buy a property in the future
            – requires drafting and negotiating an entirely separate contract
          </ListItem>
          <ListItem>
            <strong>Overage agreements</strong> – an agreement where the buyer agrees to pay the seller an extra amount
            if a condition is met in future (for example, if the property is given planning permission)
          </ListItem>
          <ListItem>
            <strong>Park homes</strong>
          </ListItem>
          <ListItem>
            Buying or selling just <em>part</em> of a property – buying two linked properties is fine, such as a house
            with a separate garage
          </ListItem>
          <ListItem>
            <strong>Part-exchange</strong>
          </ListItem>
          <ListItem>
            Buying on behalf of someone using a <strong>power of attorney</strong>, including Lasting Powers of Attorney
          </ListItem>
          <ListItem>
            <strong>Separate lender representation</strong> – acting only for you and not for your mortgage lender as
            well
          </ListItem>
          <ListItem>
            <strong>Separation</strong>
          </ListItem>
          <ListItem>
            <strong>Reclaiming overpaid Stamp Duty</strong>
          </ListItem>
          <ListItem>
            <strong>Remortgaging</strong>
          </ListItem>
          <ListItem>
            Advising on <strong>rental problems</strong> – for example, apportionment of rent, deposits, issues with a
            landlord
          </ListItem>
          <ListItem>
            <strong>Repossession</strong>
          </ListItem>
          <ListItem>
            Buying from a seller who is <strong>representing themself</strong>
          </ListItem>
          <ListItem>
            Buying a <strong>retirement property</strong> – as these involve special fees and agreements
          </ListItem>
          <ListItem>
            <strong>Right to Buy</strong> – buying a property direct from the council for a discount – as this involves
            a special process with no contract
          </ListItem>
          <ListItem>
            Properties in <strong>Scotland</strong>
          </ListItem>
          <ListItem>
            Buying with <strong>no searches</strong>
          </ListItem>
          <ListItem>
            <strong>Shared ownership</strong> – buying a share of a property from a council or a property developer
          </ListItem>
          <ListItem>
            <strong>Splitting one property into two</strong>
          </ListItem>
          <ListItem>
            <strong>Split transactions</strong> – for example, we’re handling the sale and another firm are handling the
            purchase
          </ListItem>
          <ListItem>
            <strong>Staircasing</strong> – buying a larger share of a property from a council or a property developer,
            or selling the whole property when you own a share only
          </ListItem>
          <ListItem>
            <strong>Static caravans</strong>
          </ListItem>
          <ListItem>
            <strong>Student accommodation</strong>
          </ListItem>
          <ListItem>
            <strong>Student mortgages</strong>
          </ListItem>
          <ListItem>
            <strong>Tenants</strong> – unless the tenant is buying
          </ListItem>
          <ListItem>
            <strong>Transfer of equity</strong> – adding or removing someone from the ownership of the property,
            including giving a property to someone for no cost
          </ListItem>
          <ListItem>
            Acting for a <strong>trust</strong>
          </ListItem>
          <ListItem>
            <strong>Tyneside leases</strong>
          </ListItem>
          <ListItem>
            <strong>University mortgages</strong>
          </ListItem>
          <ListItem>
            <strong>Unregistered land</strong>, including where the registration is pending, and leaseholds where the
            underlying freehold is unregistered
          </ListItem>
        </List>
        <p>If we discover after starting work that any of the above apply to your transaction:</p>
        <List>
          <ListItem>We may need to ask you to use another law firm.</ListItem>
          <ListItem>
            If you have given us inaccurate information (for example, you’ve said that the property does not have
            tenants, but it does) we may not be able to refund your fees.
          </ListItem>
          <ListItem>
            If we can continue acting for you (which will be at our sole discretion) we’ll let you know what additional
            fee will be payable for the extra work required.
          </ListItem>
        </List>

        <Heading level="3" id="checking-your-identity">
          Checking your identity
        </Heading>
        <p>We have to verify the identity of individuals and companies who are our clients.</p>
        <p>To verify the identity of individuals we can accept a valid:</p>
        <List bullet>
          <ListItem>Passport</ListItem>
          <ListItem>Photocard driving licence</ListItem>
          <ListItem>For people outside the UK, a national ID card</ListItem>
        </List>
        <p>If a company is buying, selling or remortgaging a property, we’ll also need to check the identity of:</p>
        <List bullet>
          <ListItem>directors of the company</ListItem>
          <ListItem>
            any person who has a 25% or greater beneficial ownership of the company (directly or indirectly)
          </ListItem>
          <ListItem>
            the directors of any company that has a 25% or greater beneficial ownership of the company
          </ListItem>
        </List>
        <p>
          We’ll do our best to help you provide the information we need, but if you do not pass our ID checks, we will
          not be able to work for you.
        </p>
        <p>
          We use Onfido to carry out our identity checks. This involves processing biometric data — a face-matching
          check between you and your ID document.
        </p>

        <Heading level="3" id="making-sure-your-money-has-come-from-legitimate-sources">
          Making sure your money comes from legitimate sources
        </Heading>
        <p>
          Every law firm is required to make sure that any money used toward a property transaction has come from
          legitimate sources.
        </p>
        <p>
          We’ll start by asking for six months’ bank statements. We may have to ask for more documents. The exact type
          and amount of documentation we need for these checks is up to us.
        </p>
        <p>
          If someone else is giving or lending you money, they’ll also need to pass our identity checks and show that
          the money they’re providing has come from legitimate sources.
        </p>
        <p>
          We cannot accept money which originated in arms, art, cryptocurrency, gambling, gemstones, mining, precious
          metals. We also cannot accept money from any source we cannot reasonably investigate, or that creates a risk
          unacceptable to us. For example, we cannot accept money which originated in a high risk country, and this
          applies even if the money is now in a UK bank account. If you’d like to check whether we can accept your
          money, please just ask.
        </p>
        <p>If you have money from a source we cannot accept, you have two options:</p>
        <List bullet>
          <ListItem>
            You can find a different UK-based source for the money – such as another savings account, or a relative who
            can give you an unconditional gift.
          </ListItem>
          <ListItem>
            You can use another law firm which is set up to verify money from this source, such as a bilingual law firm,
            or a firm with a cryptocurrency specialisation, and we would refund any fees not yet transferred to our
            company account.
          </ListItem>
        </List>
        <p>
          We’ll do our best to help you provide the information we need, but if we cannot verify that your money is from
          legitimate sources, we will not be able to work for you.
        </p>

        <Heading level="3" id="payments">
          Payments
        </Heading>
        <p>We accept payments via:</p>
        <List bullet>
          <ListItem>bank transfer from a UK-based account</ListItem>
          <ListItem>credit or debit card (via our website, for upfront payments only)</ListItem>
        </List>
        <p>We do not accept:</p>
        <List bullet>
          <ListItem>cash</ListItem>
          <ListItem>cheques</ListItem>
          <ListItem>bank transfers from accounts held outside the UK</ListItem>
          <ListItem>card payments over the phone</ListItem>
          <ListItem>magic beans</ListItem>
        </List>

        <Heading level="3" id="staying-safe">
          Staying safe
        </Heading>
        <p>
          <strong>Read this bit extra carefully!</strong> Property transactions are a big target for fraudsters.
        </p>
        <p>Once you sign up, we’ll send the following by post to your home address:</p>
        <List bullet>
          <ListItem>Our bank details</ListItem>
          <ListItem>A form for you to tell us your bank details</ListItem>
        </List>
        <p>Stay safe by remembering these simple rules:</p>
        <List bullet>
          <ListItem>
            Our bank details <strong>will not change</strong> for the whole of your transaction.
          </ListItem>
          <ListItem>
            We’ll <strong>never</strong> call or email you to tell you our bank details have changed.
          </ListItem>
          <ListItem>
            If someone tells you our bank details have changed,{" "}
            <strong>they’re a fraudster. You should let us know right away by phone on 020 3856 3339</strong>. Please
            don’t email us about suspected fraud.
          </ListItem>
        </List>
        <p>
          If we need to transfer money to you, we’ll only send that money to a UK bank account in your name. If you need
          to send the money to someone else, we’ll send it to you, and then you can send it to the other person.
        </p>

        <Heading level="3" id="how-we-store-your-money">
          How we store your money
        </Heading>
        <p>
          We keep all our clients’ money in client account facilities provided by UK banks authorised by the Bank of
          England. We pay our business expenses from completely separate bank accounts.
        </p>
        <p>We do not calculate or pay interest to you on money we hold on your behalf.</p>
        <p>
          In the unlikely event that a bank holding our client money were to fail, the Financial Services Compensation
          Scheme (FSCS) protects clients up to £1million for funds related to a residential property transaction
          (otherwise up to £85,000). To enable any payout, we may need to share information identifying you as the
          underlying client. We rely on legal obligation for any such disclosure.
        </p>

        <Heading level="3" id="returning-money-to-you">
          Returning money to you
        </Heading>
        <p>
          If there’s money left over once your transaction is finished and we have your payment details, we aim to
          return the money to you by the end of completion day.
        </p>
        <p>If we do not have payment details, we’ll contact you to ask for them.</p>
        <p>
          If, after 12 months we’ve been unable to return an amount of less than £50 to you, we’ll donate it on your
          behalf to the charity Crisis –{" "}
          <a href="https://www.crisis.org.uk" target="_blank" rel="noreferrer">
            www.crisis.org.uk
          </a>
          . They use donations to fight homelessness.
        </p>
        <p>
          If the amount is more than £50 and we’ve been trying to return it to you for more than 12 months, we’ll ask
          our regulator for permission to donate the money to Crisis.
        </p>
        <Heading level="3" id="your-personal-information">
          Your personal information
        </Heading>
        <p>
          By accepting these terms you agree that the following people may see your personal information and detailed
          case information:
        </p>
        <List bullet>
          <ListItem>Our employees and contractors</ListItem>
          <ListItem>Your mortgage lender, if you’re buying with a mortgage</ListItem>
          <ListItem>Your mortgage broker, if you’re using one</ListItem>
          <ListItem>
            The freeholder, landlord, management companies and/or managing agents for the property you’re transacting
          </ListItem>
          <ListItem>
            Our suppliers of data processing services (described in our privacy policy at{" "}
            <a href="https://www.juno.legal/privacy" target="_blank" rel="noreferrer">
              www.juno.legal/privacy
            </a>
            )
          </ListItem>
          <ListItem>
            Other companies in the Juno group – currently Juno Legal Holdings Limited (our parent company), Juno Global
            Services Limited (our group’s technology and operations company) and Juno SA Right Now (Pty) Ltd (our
            outbound call and chase team).
          </ListItem>
          <ListItem>Our regulators (including the CLC and the FCA)</ListItem>
          <ListItem>Any official body by virtue of their legal powers</ListItem>
        </List>
        <p>
          By accepting these terms you agree that we can share non-personal information about the progress of the
          transaction with your:
        </p>
        <List bullet>
          <ListItem>Estate agents</ListItem>
          <ListItem>Mortgage lender</ListItem>
          <ListItem>Mortgage broker</ListItem>
          <ListItem>Other parties’ conveyancers in the transaction and any linked chain</ListItem>
        </List>
        <p>
          If you use a referral code or referral link to get a quote from us, then we’ll tell the person or organisation
          associated with that code or link that you got a quote. We’ll also tell them when you sign up and when your
          case completes, but we won’t include any personal details in these notifications.
        </p>
        <p>
          We’re often asked to recommend and introduce third party suppliers of home-moving services like surveying,
          removals, home insurance and life insurance. Juno Property Lawyers Limited (JPL) does not make introductions
          to third party suppliers. Any recommendations and introductions during the course of your transaction with us
          are made by a company called Juno Global Services Limited (JGS). JGS may make these recommendations or
          introductions via your dashboard or by email.
        </p>
        <p>
          We will share your personal information and case details with JGS to facilitate these introductions. JGS is
          owned and controlled by the same shareholders as Juno Property Lawyers Limited and is part of the Juno group.
          We won’t sell your information to anyone. We’ll only share it with the people and companies listed above, or
          with anyone else we need to share it with to carry out your transaction.
        </p>
        <p>
          We’re registered with the Information Commissioner’s Office (ICO) as a data controller. Our ICO number is
          ZA245867. For full details about how we use your data, your rights, and the third parties we share data with,
          see our privacy notice at{" "}
          <a href="https://www.juno.legal/privacy" target="_blank" rel="noreferrer">
            www.juno.legal/privacy
          </a>
          .
        </p>
        <p>
          We’ll record all your calls and emails with us and store them for as long as we’re required to store your file
          (see <a href="#file-storage">File storage</a> below).
        </p>
        <p>
          Juno operates as a secure, cloud-native practice, which may involve some of our authorised staff and
          contractors working on your matter and accessing relevant data from outside the UK (including the EEA,
          Switzerland, South Africa, and Pakistan). All such access is strictly controlled via secure, firm-managed
          hardware and encrypted connections. Where these people are outside the EU, these arrangements are protected by
          International Data Transfer Agreements (IDTAs) as prescribed by the ICO, ensuring your information receives a
          level of protection equivalent to that within the UK, even when being viewed by our international teams.
        </p>
        <p>
          Some of the technology providers we use also process data outside the UK — most often in the United States.
          Where this happens, we rely on UK adequacy regulations (such as the UK Extension to the EU–US Data Privacy
          Framework) or on International Data Transfer Agreements with appropriate safeguards. See our privacy notice
          for the full picture.
        </p>

        <Heading level="3" id="how-we-use-ai-tools">
          How we use AI tools
        </Heading>
        <p>
          We use AI to help us run your case. We test AI tools before we put them into use and monitor them carefully
          once they’re in use, in line with the Council for Licensed Conveyancers’ AI and Technology Principles. Our
          lawyers remain responsible for your case and the legal advice we give you.
        </p>
        <p>Our AI providers process your case data on our behalf as our data processors.</p>

        <Heading level="3" id="copyright">
          Copyright
        </Heading>
        <p>We retain the copyright for our online service and all materials we create.</p>

        <Heading level="3" id="complying-with-the-law">
          Complying with the law
        </Heading>
        <p>We need to tell the National Crime Agency (NCA) if we suspect:</p>
        <List bullet>
          <ListItem>You’re attempting to launder money or help someone launder money.</ListItem>
          <ListItem>Your money is the proceeds of crime, including tax evasion.</ListItem>
        </List>
        <p>You agree that we can do this regardless of our duty of confidentiality to you.</p>
        <p>
          If you ask us about anything relating to crime prevention, we don’t have to give an answer. For example, if
          you ask us whether we have reported you for money laundering, we can’t tell you either way.
        </p>
        <p>If the police ask us to stop acting for you, we’ll do so, and we won’t be able to tell you the reason.</p>

        <Heading level="3" id="our-liability">
          Our liability
        </Heading>
        <p>
          If we make a mistake that ends up causing a problem or costing you money, you can make a claim against our
          professional indemnity insurance.
        </p>
        <p>You agree that:</p>
        <List bullet>
          <ListItem>
            Any claim you want to make will be against Juno, and not against any individuals who work for us.
          </ListItem>
          <ListItem>
            The limit of our insurance is £4 million per claim, and this is the largest claim you can make.
          </ListItem>
          <ListItem>
            If we can’t deliver our services to you through no fault of our own and because of circumstances outside of
            our control, you won’t have any claim against us for any actual or consequential loss. For example, if there
            are delays in the banking system.
          </ListItem>
          <ListItem>
            If you make a valid claim against us for a loss arising out of work for which we’re legally responsible and
            we’re unable to meet our liability in full, you may be entitled to claim from the Council for Licensed
            Conveyancers compensation fund. Contact the CLC if you need more information.
          </ListItem>
        </List>

        <Heading level="4" id="transactions-which-do-not-complete-or-miss-a-deadline">
          Transactions which do not complete, or miss a deadline
        </Heading>

        <p>
          We always try our best to bring your transaction to completion, and if you’ve asked us to hit a deadline we
          will try our best to meet it. But we do not guarantee that your transaction will complete, or that it will
          complete by a given deadline. For example, your buyer or seller may decide not to proceed or not to meet your
          deadline, and we cannot force them to do so.
        </p>
        <p>
          We are not liable for any actual or consequential loss if your transaction does not complete, or does not
          complete by a specified date.
        </p>
        <p>For example:</p>
        <List bullet>
          <ListItem>
            If your sale did not proceed, we would not be liable for your mortgage repayments, council tax or utility
            bills.
          </ListItem>
          <ListItem>
            We would not be liable if your sale did not complete by a deadline and you were not able to reclaim higher
            rate Stamp Duty paid on a previous purchase, or had to pay a higher rate of Stamp Duty.
          </ListItem>
          <ListItem>
            If you have to leave your current accommodation – for example, if your landlord has given you notice – we
            would not be liable for any losses or costs you incur if your purchase did not complete in time for you to
            move straight into the new property.
          </ListItem>
        </List>
        <p>
          If someone who works for Juno says that we will complete a transaction or that we will meet a deadline, this
          is not a guarantee that we will be able to do so, and these terms will continue to apply.
        </p>

        <Heading level="4" id="leasehold-properties-and-the-building-safety-act-2022">
          Leasehold properties and the Building Safety Act 2022
        </Heading>
        <p>
          The Building Safety Act 2022 (BSA) protects leaseholders from the cost of fire safety issues in several ways.
        </p>
        <p>
          Mortgage lenders have strict requirements for properties that fall under the BSA. We reserve the right to stop
          acting for you if cannot meet your lender’s BSA requirements. In particular, we cannot advise you on:
        </p>
        <List bullet>
          <ListItem>
            <strong>Building height</strong> – we’re not experts on this, you’ll need to talk to your surveyor.
          </ListItem>
          <ListItem>
            <strong>Whether a building is or is not in scope of the BSA</strong> – building height is a key aspect of
            this, so you’ll need to talk to your surveyor.
          </ListItem>
          <ListItem>
            <strong>The contents of a Landlord’s Certificate</strong> (as defined in the BSA) – as we are not experts on
            assessing whether a landlord meets qualifying criteria, especially in relation to their financial position.
          </ListItem>
        </List>

        <Heading level="4" id="limit-of-our-liability">
          Limit of our liability
        </Heading>
        <p>
          We can only limit our liability to the extent that the law allows. In particular we can’t limit our liability
          for death or personal injury caused by our negligence.
        </p>

        <Heading level="3" id="online-reviews">
          Online reviews
        </Heading>
        <p>
          If you leave us an online review, you agree that we can respond. If a review contains factual inaccuracies
          about the conduct of your case or our service, we can correct those inaccuracies in our response.
        </p>

        <Heading level="3" id="file-storage">
          File storage
        </Heading>
        <p>We keep your information for several years after we complete your transaction:</p>
        <List bullet>
          <ListItem>If you sell a property, we’ll keep your file for 6 years.</ListItem>
          <ListItem>If you buy or remortgage a property, we’ll keep your file for 15 years.</ListItem>
        </List>
        <p>When a transaction is complete and you have paid our fees, we’ll provide you with:</p>
        <List bullet>
          <ListItem>Electronic copies of any documents we obtained for you</ListItem>
          <ListItem>A record of all significant correspondence sent and received as part of the transaction</ListItem>
        </List>
        <p>We will provide copies of any documents on request, as long as you have paid our fees.</p>
        <p>We don’t keep physical copies of documents once a transaction is completed and registered.</p>

        <Heading level="3" id="contacting-you-in-future">
          Contacting you in future
        </Heading>
        <p>
          We or our companies in the Juno group may contact you about other services that we think will be useful — for
          example, an offer on a remortgage 18 months into a two-year fixed term. You can opt out at any time by
          emailing us, clicking the unsubscribe link in any message, or replying to ask us to stop.
        </p>

        <Heading level="3" id="if-youre-not-happy-our-complaints-policy">
          If you’re not happy – our complaints policy
        </Heading>
        <p>
          If you’re not happy with our service, start by telling the person handling your case. We’ll do our best to
          turn things around for you.
        </p>
        <p>
          Please do tell us if you’re not happy – we want to give you a good experience, the earlier you tell us, the
          more we can do to put things right.
        </p>
        <p>If you want to make a complaint you can:</p>
        <List bullet>
          <ListItem>
            Email us at <a href="mailto:hello@juno.legal">hello@juno.legal</a>
          </ListItem>
          <ListItem>Send a letter to us at Juno, 43–51 New North Road, London N1 6LU</ListItem>
          <ListItem>
            Call us on <a href="tel:+442038563339">020 3856 3339</a> and tell us that you want to make a complaint
          </ListItem>
        </List>
        <p>Making a complaint is free.</p>

        <Heading level="4" id="what-happens-after-you-make-a-complaint">
          What happens after you make a complaint
        </Heading>
        <p>
          Your complaint will be reviewed by the leader of the team handling your case, supported by the Head of Client
          Service. The team lead will start a detailed and fair investigation as soon as we receive your complaint.
        </p>
        <p>
          We’ll always reply within 7 days of receiving your complaint, either to reply in full or to confirm how your
          complaint will be handled. In all cases you’ll receive a full response within 28 days.
        </p>
        <p>
          Our written response to your complaint will always be sent by email. If you made your complaint by phone,
          we’ll summarise our understanding of your complaint in our written response.
        </p>
        <p>
          If we’ve got something wrong we’ll say so, apologise, and do what we can to put it right, which may include a
          reduction in our fee or compensation.
        </p>

        <Heading level="4" id="what-to-do-if-you-are-not-happy-with-our-response">
          What to do if you are not happy with our response
        </Heading>
        <p>
          If you’re still unhappy or if you don’t feel comfortable discussing things with the person handling your case,
          please email our Head of Client Service, Rosie Jones:{" "}
          <a href="mailto:rosie.jones@juno.legal">rosie.jones@juno.legal</a>. She will arrange for a separate review of
          your complaint and send you a written response within 28 days.
        </p>
        <p>
          If you’re not happy following this final review, you can complain about us to the Legal Ombudsman. You must
          make your complaint to the Legal Ombudsman within six months of receiving Juno’s final response to your
          complaint, and within a year of the date of the thing you’re complaining about (or a year from the date you
          could reasonably have known there was a cause for complaint).
        </p>
        <p>The Legal Ombudsman’s details are:</p>
        <List bullet>
          <ListItem>
            {" "}
            <a href="https://www.legalombudsman.org.uk/" target="_blank" rel="noreferrer">
              www.legalombudsman.org.uk
            </a>
          </ListItem>
          <ListItem>
            <a href="tel:+443005550333">0300 555 0333</a>
          </ListItem>
          <ListItem>
            {" "}
            <a href="mailto:enquiries@legalombudsman.org.uk">enquiries@legalombudsman.org.uk</a>
          </ListItem>
          <ListItem>Legal Ombudsman, PO Box 6167, Slough SL1 0EH</ListItem>
        </List>
        <p>
          You can also go to the Legal Ombudsman if we have not resolved your complaint within eight weeks of receiving
          it.
        </p>
        <p>
          Please note that the Ombudsman deals with complaints about the service you received. Complaints about the
          conduct of our staff made to the Legal Ombudsman will be referred to our regulator, the Council for Licensed
          Conveyancers.
        </p>
        <p>
          We don’t agree to use alternative complaint organisations, for example, Ombudsman Services, ProMediate, Small
          Claims Mediation.
        </p>

        <Heading level="3" id="dont-be-dishonest-rude-or-threatening">
          Don’t be dishonest, rude or threatening
        </Heading>
        <p>It’s very important that you can trust us, and we can trust you.</p>
        <p>
          If we discover that you have been dishonest with us, we believe that you have been excessively rude to our
          staff, or that you have made any form of threat against the company or our staff, then:
        </p>
        <List bullet>
          <ListItem>we will refuse to continue to act for you</ListItem>
          <ListItem>you’ll have to find new lawyers</ListItem>
          <ListItem>you won’t be entitled to a refund of any legal fees you’ve already paid us</ListItem>
        </List>

        <Heading level="3" id="our-authorisation">
          Our authorisation
        </Heading>
        <p>
          The Council of Licensed Conveyancers (CLC) –{" "}
          <a href="https://www.clc-uk.org" target="_blank" rel="noreferrer">
            www.clc-uk.org
          </a>{" "}
          – have authorised Juno to provide conveyancing services in England and Wales. Our licence number is 3338.
        </p>
        <p>
          We are not authorised by the Financial Conduct Authority (FCA) but we’re included in the FCA register (
          <a href="https://register.fca.org.uk" target="_blank" rel="noreferrer">
            register.fca.org.uk
          </a>
          ) as an ‘exempt professional firm’ so we can provide indemnity policies as part of conveyancing transactions.
        </p>

        <Heading level="3" id="and-finally">
          And finally…
        </Heading>
        <p>If we have to make a change to these terms, we’ll tell you in writing.</p>
        <p>
          If there’s a discrepancy between these terms and any other description of our service, these terms will apply.
        </p>
        <p>
          If we make any changes in future to the way that our company and wider corporate group is organised, we can
          transfer your cases and contract to another company under our control. If this happens then the same people
          will continue to work on your case, and these terms will continue to apply.
        </p>
        <p>
          This agreement is governed by the laws of England and Wales and is subject to the exclusive jurisdiction of
          the courts of England and Wales.
        </p>

        <Heading level="3" id="about-juno">
          About Juno
        </Heading>
        <DescriptionList>
          <DescriptionListTerm>Company name</DescriptionListTerm>
          <DescriptionListDetail>Juno Property Lawyers Limited</DescriptionListDetail>
          <DescriptionListTerm>Registered office</DescriptionListTerm>
          <DescriptionListDetail>43–51 New North Road, London N1 6LU</DescriptionListDetail>
          <DescriptionListTerm>Company number</DescriptionListTerm>
          <DescriptionListDetail>10227405</DescriptionListDetail>
          <DescriptionListTerm>
            <abbr title="Value added tax">VAT</abbr> number
          </DescriptionListTerm>
          <DescriptionListDetail>243966087</DescriptionListDetail>
          <DescriptionListTerm>Council of Licensed Conveyancers (CLC) number</DescriptionListTerm>
          <DescriptionListDetail>3338</DescriptionListDetail>
          <DescriptionListTerm>Solicitors Regulatory Authority (SRA) number</DescriptionListTerm>
          <DescriptionListDetail>644540</DescriptionListDetail>
          <DescriptionListTerm>Information Commissioner’s Office (ICO) number </DescriptionListTerm>
          <DescriptionListDetail>ZA245867</DescriptionListDetail>
          <DescriptionListTerm>Chief Executive Officer / Head of Finance and Administration</DescriptionListTerm>
          <DescriptionListDetail>Etienne Pollard</DescriptionListDetail>
          <DescriptionListTerm>Head of Legal Practice</DescriptionListTerm>
          <DescriptionListDetail>Charles Jacobs</DescriptionListDetail>
        </DescriptionList>
        <JunoTermsVersion>Version {TERMS_AND_CONDITIONS_VERSION_NUMBER}</JunoTermsVersion>
      </Text>
    </React.Fragment>
  );
}

export default JunoTermsAndConditions;
